import { Icons } from '@commenda/kakashi';
import { useMemo, useState } from 'react';

import { DataPartHelper, type IDataPartEntry } from './data-part-helper';

interface ResearchProcessBlockProps {
  entries: readonly IDataPartEntry[];
}

/** Research's working trace (every researcher step, finding, synthesis and verification) as one
 * collapsed section with a line per angle, instead of a block per step: a single run writes hundreds. */
export function ResearchProcessBlock({ entries }: ResearchProcessBlockProps) {
  const [isOpen, setIsOpen] = useState(false);
  const summary = useMemo(
    () => DataPartHelper.summarizeResearchProcess(entries),
    [entries]
  );
  const { angles, requirementCount, verdict } = summary;
  const angleLabel = angles.length === 1 ? 'angle' : 'angles';

  return (
    <div className="flex w-full flex-col gap-md">
      <button
        type="button"
        onClick={() => setIsOpen((previous) => !previous)}
        aria-expanded={isOpen}
        className="flex w-fit items-center gap-sm border-none bg-transparent p-none txt-small-medium text-text-tertiary"
      >
        <span>
          Research process · {angles.length} {angleLabel}
        </span>
        {isOpen ? (
          <Icons.ChevronDown
            className="size-3 shrink-0 text-fg-quaternary"
            aria-hidden="true"
          />
        ) : (
          <Icons.ChevronRight
            className="size-3 shrink-0 text-fg-quaternary"
            aria-hidden="true"
          />
        )}
      </button>
      {isOpen && (
        <ul className="flex flex-col gap-sm border-l border-border-primary pl-lg">
          {angles.map((angle) => (
            <li
              key={angle.angle}
              className="flex flex-col txt-small-regular text-text-secondary"
            >
              <span className="text-text-primary">{angle.angle}</span>
              <span className="txt-extra-small-regular text-text-tertiary">
                {angle.findingCount === null
                  ? `Researching · ${angle.stepCount} steps`
                  : `${angle.findingCount} findings from ${angle.stepCount} steps${angle.hitCap ? ' · stopped at the step limit' : ''}`}
              </span>
            </li>
          ))}
          {requirementCount !== null && (
            <li className="txt-small-regular text-text-secondary">
              Synthesised {requirementCount} requirements
            </li>
          )}
          {verdict && (
            <li className="flex flex-col txt-small-regular text-text-secondary">
              <span>
                Verified {verdict.verifiedCount} of {verdict.checkedCount}{' '}
                fields
              </span>
              {verdict.gaps.length > 0 && (
                <span className="txt-extra-small-regular text-text-tertiary">
                  Gaps: {verdict.gaps.join('; ')}
                </span>
              )}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
